"use client";

import { usePassportProvider } from "@/context";
import { useCheckout } from "@/context/CheckoutProvider";
import { useFiatPricingContext } from "@/context/FiatPricingProvider";
import { useLoginModal } from "@/context/LoginModalContext";
import { useCoinBalancesZkEVMQuery } from "@/hooks/useQuery";

import { useMemo } from "react";
import { BalanceList } from "./balance-list";
import {
  filterNonZeroBalances,
  transformBalanceToken,
} from "./data-transformers";
import type { BalancesListProps } from "./types";

const mockBalances = [
  {
    symbol: "Quest",
    name: "Quest",
    balance: "100",
    decimals: 18,
    imageUrl: "/images/tokens/quests.png",
    contractAddress: "0x123...",
    fiatEquivalentPrice: {
      amount: "0.07",
      currency: "USD",
      prefixSymbol: "$",
    },
    dailyChange: -2.0,
    currentPrice: 0.0007,
  },
  {
    symbol: "ETH",
    name: "Ethereum",
    balance: "1",
    decimals: 18,
    imageUrl: "/images/tokens/eth.png",
    contractAddress: "0x456...",
    fiatEquivalentPrice: {
      amount: "3,896.62",
      currency: "USD",
      prefixSymbol: "$",
    },
    dailyChange: 7.4,
    currentPrice: 3896.62,
  },
  {
    symbol: "GOG",
    name: "Guild of Guardians",
    balance: "12,000",
    decimals: 18,
    imageUrl: "/images/tokens/gog.png",
    contractAddress: "0x789...",
    fiatEquivalentPrice: {
      amount: "520.00",
      currency: "USD",
      prefixSymbol: "$",
    },
    dailyChange: 0,
    currentPrice: 0.043,
  },
];

export function BalancesList({ className }: BalancesListProps) {
  const { walletAddress, isLoggedIn } = usePassportProvider();
  const { checkoutClient, checkoutNetwork, checkoutProvider } = useCheckout();
  const { fiatPrices, buildFiatEquivalentPrice, getFiatPriceForSymbol } =
    useFiatPricingContext();
  const { openLoginModal } = useLoginModal();

  const {
    data: coinBalances,
    error: balanceError,
    isLoading: balanceLoading,
  } = useCoinBalancesZkEVMQuery(
    walletAddress,
    checkoutClient,
    checkoutNetwork,
    checkoutProvider,
  );

  const tokens = useMemo(() => {
    if (!coinBalances) {
      return [];
    }

    const transformedTokens = coinBalances.map((coinBalance) => {
      const fiatEquivalentPrice = buildFiatEquivalentPrice(
        coinBalance.balance,
        coinBalance.decimals,
        coinBalance.symbol,
        fiatPrices,
      );
      const tokenUsdPrice = getFiatPriceForSymbol(coinBalance.symbol);
      return transformBalanceToken(
        coinBalance,
        fiatEquivalentPrice,
        tokenUsdPrice,
      );
    });

    const nonZeroTokens = filterNonZeroBalances(transformedTokens);

    return nonZeroTokens.length > 0 ? nonZeroTokens : transformedTokens;
  }, [
    coinBalances,
    fiatPrices,
    buildFiatEquivalentPrice,
    getFiatPriceForSymbol,
  ]);

  if (!isLoggedIn) {
    return (
      <BalanceList
        tokens={mockBalances}
        isLoading={false}
        error={null}
        className={className}
      />
    );
  }

  return (
    <BalanceList
      tokens={tokens}
      isLoading={balanceLoading}
      error={balanceError}
      className={className}
    />
  );
}

export { BalanceList } from "./balance-list";
export { BalanceItem } from "./balance-item";
